// İSG Portal — Eğitim & Test (Training — tenant-scoped) API çağrıları + tipleri.
// **İSG uzmanı tarafı** (web-expert authoring — §16). Liste yüzeyi (İş İzni gerekli-eğitim
// seçicisi §17.2 aktif eğitimleri buradan çeker) + **tam authoring** (get/create/update/
// delete + video referansı + soru seti; parça 051).
//
// KAYNAK: api-contract.md §16 (Eğitim & Test). Backend kanonu; endpoint yolu + alanlar +
// kimlik kuralları (Create → `value.uuid` + 201; liste → `value` + `total`/`filteredTotal`)
// doğrudan sözleşmedendir. Serileştirme camelCase (§1).
//
// **⛔ CEVAP ANAHTARI (`correctOptionIndex`) — yalnız authoring (§16).** Doğru seçeneğin
// 0-tabanlı indeksi YALNIZ bu authoring DTO'sunda (`TrainingQuestionDto`) döner — `training`
// yetkili İSG uzmanı içindir. Personel/mobil tamamlama bağlamı (parça 049) `correctOptionIndex`'i
// ASLA görmez (ayrı "çözme" DTO'su; cevap anahtarı yok). Bu istemci de cevap anahtarını yalnız
// eğitim editöründe gösterir/düzenler; başka hiçbir yüzeye taşımaz.

import { api } from './client';
import type { Empty } from './envelope';

/**
 * Eğitim liste satırı — api-contract §16 (`TrainingDto`). Özet: soru seti/cevap anahtarı
 * taşımaz. `hasVideo` = video referansı var mı; `questionCount` = aktif soru sayısı;
 * `passThreshold` = geçme eşiği (doğru sayısı). Sıralı iç `Id` istemciye **sızmaz**.
 */
export interface TrainingDto {
  uuid: string;
  title: string;
  description: string | null;
  hasVideo: boolean;
  questionCount: number;
  passThreshold: number;
  isActive: boolean;
  createdAt: string | null;
}

/**
 * Eğitim liste/filtre parametreleri — api-contract §16.1 (`GET /trainings`) + §3 paging.
 * `search` (başlık substring, case-insensitive), `isActive` (verilmezse hepsi). Sıra
 * backend'de oluşturulma azalan (istemci sort göndermez).
 */
export interface TrainingListParams {
  search?: string;
  isActive?: boolean;
  page?: number;
  pageSize?: number;
}

/**
 * Eğitim testi sorusu — api-contract §16 (`TrainingQuestionDto`, **authoring — cevap
 * anahtarı dahil**). `options` = seçenek metinleri (2-5; sıra anlamlı); `correctOptionIndex`
 * = doğru seçeneğin 0-tabanlı indeksi (`options` aralığında). **Yalnız §16 authoring uçları
 * döner** — parça 049 (personel/mobil) `correctOptionIndex`'i ASLA döndürmez.
 */
export interface TrainingQuestionDto {
  uuid: string;
  order: number;
  text: string;
  options: string[];
  correctOptionIndex: number;
}

/**
 * Eğitim detayı — api-contract §16 (`TrainingDetailDto`; `GET /trainings/{uuid}` + create/
 * update dönüşü). Liste özetine (`TrainingDto`) ek olarak video referansı (`videoDocumentUuid`
 * / §8'den çözülen `videoFileName`) + **tam soru seti** (`questions`, cevap anahtarı dahil)
 * taşır. Sıralı iç `Id` istemciye **sızmaz**.
 */
export interface TrainingDetailDto extends TrainingDto {
  /** §8 Storage'daki confirmed video dosyasının uuid'i (yoksa null). */
  videoDocumentUuid: string | null;
  /** §8'den çözülen video dosya adı (opsiyonel/null). */
  videoFileName: string | null;
  /** Görüntüleme sırasına göre sorular (**authoring — cevap anahtarı dahil**). */
  questions: TrainingQuestionDto[];
}

/**
 * Soru yazma girdisi — api-contract §16.1 (create/update gövdesi). `order` görüntüleme sırası
 * (1-tabanlı); `options` 2-5 seçenek metni (boş olamaz); `correctOptionIndex` seçenek
 * aralığında olmalı. Sunucu doğrular (aksi `400 VALIDATION_FAILED`).
 */
export interface TrainingQuestionInput {
  order: number;
  text: string;
  options: string[];
  correctOptionIndex: number;
}

/**
 * Eğitim oluşturma/güncelleme isteği — api-contract §16.1 (`POST /trainings` /
 * `PUT /trainings/{uuid}`; aynı gövde). `title` zorunlu; `videoDocumentUuid` verilirse
 * (null değilse) aynı firmada + **confirmed** bir §8 dosyası olmalı (yok/başka firma/pending
 * → `404 DOCUMENT_NOT_FOUND`); `questions` **en az 1 soru**; `passThreshold` 1..soru sayısı.
 * PUT'ta soru seti **replace** edilir (eski soft-delete, yeni set). `videoDocumentUuid` null →
 * video bağı kaldırılır.
 */
export interface TrainingWriteRequest {
  title: string;
  description?: string | null;
  videoDocumentUuid?: string | null;
  passThreshold: number;
  isActive: boolean;
  questions: TrainingQuestionInput[];
}

/**
 * `GET /trainings` (`list`) — firmanın eğitimleri (sayfalı + filtreli). İş İzni gerekli-
 * eğitim seçicisi `isActive: true` ile aktif eğitimleri çeker (§17.2 yalnız aktif eğitim
 * gerekli olabilir).
 */
export function listTrainings(params: TrainingListParams = {}) {
  return api.list<TrainingDto>('trainings', {
    searchParams: {
      search: params.search && params.search.trim().length > 0 ? params.search.trim() : undefined,
      isActive: params.isActive,
      page: params.page,
      pageSize: params.pageSize,
    },
  });
}

/**
 * `GET /trainings/{uuid}` (`get`) — tek eğitim + video referansı + **tam soru seti**
 * (authoring — cevap anahtarı dahil). Yok/kapsam dışı → `404 TRAINING_NOT_FOUND`.
 */
export function getTraining(uuid: string) {
  return api.get<TrainingDetailDto>(`trainings/${uuid}`);
}

/**
 * `POST /trainings` (`create`) — eğitim + soru seti oluşturur; yeni kimlik `value.uuid` (201).
 * Doğrulama §16.1 (title/questions/passThreshold/options/correctOptionIndex).
 */
export function createTraining(body: TrainingWriteRequest) {
  return api.post<TrainingDetailDto>('trainings', { json: body });
}

/**
 * `PUT /trainings/{uuid}` (`update`) — eğitim alanlarını günceller + **soru setini replace**
 * eder. Yok/kapsam dışı → `404 TRAINING_NOT_FOUND`. Doğrulama create ile aynı.
 */
export function updateTraining(uuid: string, body: TrainingWriteRequest) {
  return api.put<TrainingDetailDto>(`trainings/${uuid}`, { json: body });
}

/** `DELETE /trainings/{uuid}` (`delete`) — eğitimi **soft-delete** eder (soru seti de kapanır). */
export function deleteTraining(uuid: string) {
  return api.delete<Empty>(`trainings/${uuid}`);
}
